import { Component, ElementRef, ViewChild, OnInit, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import * as d3 from 'd3';
import { NewSearchService } from '@app/services/new-search.service';

@Component({
    selector: 'state-counties',
    templateUrl: './state-counties.component.html',
    styleUrls: ['./state-counties.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: []
})
export class StateCountiesComponent implements OnInit, OnDestroy{

  @ViewChild('mapContainer') private mapContainer!: ElementRef;
  @Input() stateId!: string;
  @Input() countyId: string = '000';
  @Input() offeringType: string;
  //@Output() countySelected = new EventEmitter<string>();
  @Input() geoJSON: any | null = null;

  private previousCounty: any = null;
  private mapG: any = null;
  private path: any = null;
  private svg: any = null;
  private zoom: any = null;
  private counties: any = null;

  private width = 550;
  private height = 350;

  private searchSubscription?: Subscription;

  constructor(private route: ActivatedRoute, private router: Router,
    private searchService: NewSearchService) {
    //this.stateId = this.route.snapshot.paramMap.get('stateId')!;
  }

  ngOnInit(): void {
    //this.createMap();
    
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  ngOnChanges(): void {

    // Set up the map dimensions
    const width = 550;
    const height = 350;
    
    if (!this.geoJSON || this.geoJSON.features[0].properties.STATE !== this.stateId) { 
      // kill any previously running searches first
      this.searchSubscription?.unsubscribe();

      this.searchSubscription = this.searchService.getStateMapGeoJSON(this.stateId, this.offeringType).subscribe({
        next: (geoJSON: string) => {
          this.geoJSON = geoJSON;
          this.mapContainer.nativeElement.append(this.getMap());
          if (this.countyId != '000') {
            this.mapContainer.nativeElement.querySelector("#C" + this.countyId).style.fill = 'green';
          }
        },
        error: (err: any) => {
          this.geoJSON = null;
          this.mapContainer.nativeElement.innerHTML = 'Error loading map';
          
        }
      });
    } else {
      if (this.countyId != this.previousCounty) {
        this.zoomToCounty();
        if (this.countyId != '000') {
          this.mapContainer.nativeElement.querySelector("#C" + this.countyId).style.fill = 'green';
        }
      }
    }

    
    
   
  }

  getMap() {
    // Set up the map dimensions
    if (!this.stateId || !this.geoJSON) {
      return '';
    }
    const zoom = d3.zoom()
        .scaleExtent([1, 8])
        .on('zoom', zoomed);
      
    this.zoom = zoom;

    
    // remove anything left in the container from before
    d3.select(this.mapContainer.nativeElement).selectAll("*").remove();
    // Create the SVG element within the container
    //const svg = d3.select(this.mapContainer.nativeElement)
    //  .append('svg')
    const svg = d3.create('svg')
      .attr('viewBox', `0 0 ${this.width} ${this.height}`)
      .attr('width', this.width)
      .attr('height', this.height)
      .attr('style', 'max-width: 100%; height: 100%;')
      .on('click', reset);
    
    this.svg = svg;

    // Calculate the bounding box of the GeoJSON data
    const bounds = d3.geoBounds(this.geoJSON);
    const dx = bounds[1][0] - bounds[0][0];
    const dy = bounds[1][1] - bounds[0][1];
    const x = (bounds[0][0] + bounds[1][0]) / 2;
    const y = (bounds[0][1] + bounds[1][1]) / 2;
    let scale = Math.min(this.width / dx, this.height / dy) * 28; // Adjust the scale factor as needed
    //const translate = [width / 2 - scale * x, height / 2 - scale * y];

    const projection = d3.geoAlbers()
      .scale(scale)
      .translate([0, 0]);

    projection.fitSize([this.width, this.height], this.geoJSON);

    const path = d3.geoPath().projection(projection);
    this.path = path;

    const g = svg.append('g');
    this.mapG = g;

      
    // Bind the GeoJSON data and create paths
    const counties = g.append('g')
      .attr('cursor', 'pointer')
      .selectAll('path')
      .data(this.geoJSON.features)
      .join('path')
      .attr('d', path)
      .attr('id', (d: any) => 'C' + d.properties.COUNTY)
      .attr('fill', (d: any) => d.properties.COLOR)
      .attr('stroke', 'black')
      .attr('stroke-width', '1')
      .on('click', clicked);

    counties.append('title')
      .text((d: any) => d.properties.NAME);
    this.counties = counties;
    // Add text labels for each county
    const labels = g.selectAll('text')
      .data(this.geoJSON.features)
      .join('text')
      .attr('x', (d: any) => path.centroid(d)[0])
      .attr('y', (d: any) => path.centroid(d)[1])
      .attr('text-anchor', 'middle')
      .attr('font-size', '8px')
      .attr('font-weight', 'bold')
      .attr('fill', 'black')
      .attr('visibility', 'hidden')
      .text((d: any) => d.properties.NAME)
      .style('pointer-events', 'none'); // Prevent the text from interfering with clicks

    g.append('path')
      .attr('fill', 'none')
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .attr('stroke-linejoin', 'round')
      .attr('d', path);

      // is there a county selected
      if (this.countyId && this.countyId !== '0') {
        
  
        const countyElement = this.geoJSON.features .find((d: any) => d.properties.COUNTY === this.countyId);
  
        // show newly clicked county label
        this.mapG.selectAll('text')
        .filter((labelData: any) => labelData === countyElement)
        .attr('visibility', 'visible')
        .attr('fill', 'white');
  
        // hide all other county labels
        this.mapG.selectAll('text')
        .filter((labelData: any) => labelData !== countyElement)
        .attr('visibility', 'hidden');
        
  
        const [[x0, y0], [x1, y1]] = this.path.bounds(countyElement);
  
        
      
        this.svg.transition().duration(750).call(
          this.zoom.transform,
          d3.zoomIdentity
            .translate(this.width / 2, this.height / 2)
            .scale(Math.min(8, 0.9 / Math.max((x1 - x0) / this.width, (y1 - y0) / this.height)))
            .translate(-(x0 + x1) / 2, -(y0 + y1) / 2)
          
        );
  
        
      }
     
    svg.call(zoom);
    //d3.select(this.mapContainer.nativeElement).append(() => svg.node()!);


      
  
  function reset() {
    counties.transition().style('fill', (d: any) => d.properties.COLOR); // Reset to original colors
    svg.transition().duration(750).call(
      zoom.transform,
      d3.zoomIdentity,
      d3.zoomTransform(svg.node()).invert([this.width / 2, this.height / 2])
    );
  }

  let previousCounty: any = null;

  function clicked(event: any, d: any) {
    event.stopPropagation();
    onCountyClick(d);
    
  }


  function zoomed(event: any) {
    const { transform } = event;
    g.attr('transform', transform);
    g.attr('stroke-width', 1 / transform.k);
  }

  const onCountyClick = (county: any) => {
    //this.countySelected.emit(county.properties);
    this.router.navigate(['/newsearch', county.properties.STATE, county.properties.COUNTY]);
  };
  
  return svg.node()
      
  }

  zoomToCounty() {  
    this.counties.transition().style('fill', (d: any) => d.properties.COLOR); // Reset to original colors
    // hide all county labels
    this.mapG.selectAll('text')
      .attr('visibility', 'hidden');
        
      // is there a county selected?
      if (this.countyId && this.countyId !== '000') {
        d3.select("#C" + this.countyId).transition().style('fill', 'green');
  
        const countyElement = this.geoJSON.features.find((d: any) => d.properties.COUNTY === this.countyId);
  
        // show newly clicked county label
        this.mapG.selectAll('text')
        .filter((labelData: any) => labelData === countyElement)
        .attr('visibility', 'visible')
        .attr('fill', 'white');
  
        
  
        const [[x0, y0], [x1, y1]] = this.path.bounds(countyElement);
        const firstTranslateFirstValue = this.width / 2;
        const firstTranslateSecondValue = this.height / 2;
        const scaleValue = Math.min(8, 0.9 / Math.max((x1 - x0) / this.width, (y1 - y0) / this.height));
        const secondTranslateFirstValue = -(x0 + x1) / 2;
        const secondTranslateSecondValue = -(y0 + y1) / 2;
        this.svg.transition().duration(750).call(
          this.zoom.transform,
          d3.zoomIdentity
            .translate(firstTranslateFirstValue, firstTranslateSecondValue)
            .scale(scaleValue)
            .translate(secondTranslateFirstValue, secondTranslateSecondValue)
          
        );
        
        
      } else {
        // reset to zoomed out view
        this.svg.transition().duration(750).call(
          this.zoom.transform,
          d3.zoomIdentity,
          d3.zoomTransform(this.svg.node()).invert([this.width / 2, this.height / 2])
        );
        
      }
      this.svg.call(this.zoom);
      if (this.previousCounty != this.countyId) {
        this.previousCounty = this.countyId;
      }
    }
}
